import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';

import { comoErroDaApi } from '../../../shared/api/erros';
import { formatarValorEmReais } from '../../../shared/dominio/moeda';
import { EstadoDeCarregamento } from '../../../shared/ui/EstadoDeCarregamento';
import { EstadoDeErro } from '../../../shared/ui/EstadoDeErro';
import { EstadoVazio } from '../../../shared/ui/EstadoVazio';
import { opcoesDoDiff } from './api/revisao';
import {
  DICAS_DO_MOTIVO_DO_DIFF,
  ROTULOS_DE_TIPO_DE_REGRA,
  ROTULOS_DO_MOTIVO_DO_DIFF,
  type ItemDoDiff,
  type MotivoDoDiffDoItem,
} from './api/tipos';
import { ReferenciaDaFonte } from './ReferenciaDaFonte';

/** Verde = novo, vermelho = não aparece, amarelo = alterado: a cor já diz o que a palavra diz. */
const COR_DO_MOTIVO: Record<MotivoDoDiffDoItem, 'success' | 'error' | 'warning'> = {
  entrou: 'success',
  saiu: 'error',
  mudou: 'warning',
};

/**
 * Uma linha do diff com o botão de aprovação individual (`TP-050`, T8:
 * "itens com confianca_unidade média ou baixa pedem aprovação individual").
 *
 * A API (`GET /fontes/{id}/diff`) não expõe `confianca_unidade` — só o
 * código, o antes/depois e o motivo (`Added`/`Removed`/`Changed`). Sem esse
 * campo, o botão de aprovação individual aparece em todo item que ENTROU ou
 * MUDOU (tem `depois`) — são exatamente os itens que a atualização da fonte
 * introduziu ou alterou, e é sobre eles que a aprovação de
 * `POST /itens-contratuais/{id}/aprovar` faz sentido (um item que só SAIU
 * não tem mais `id` atual para aprovar). Registrado como `DI28` do
 * `TP-050`: a contagem AUTORITATIVA de pendência continua sendo a que
 * `POST /fontes/{id}/ativar` devolve.
 */
function LinhaDoItem({ item, aoAprovarItem }: { item: ItemDoDiff; aoAprovarItem: (itemId: string) => Promise<void> }): React.JSX.Element {
  const [emCurso, definirEmCurso] = useState(false);
  const [aprovado, definirAprovado] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);
  // `const`, nunca reatribuído: o narrowing de `depois !== null` persiste dentro do closure do
  // `onClick` abaixo (sem precisar de `item.depois!`, que o lint proíbe).
  const { depois } = item;
  // O item que vale é o de depois; um item que só saiu mostra o que era.
  const atual = depois ?? item.antes;

  return (
    <TableRow hover>
      <TableCell>{item.codigo ?? '—'}</TableCell>
      <TableCell sx={{ minWidth: 220 }}>{atual?.descricao ?? '—'}</TableCell>
      <TableCell>{atual?.unidadeDeCobranca ?? '—'}</TableCell>
      <TableCell>{atual !== null ? String(atual.pagina) : '—'}</TableCell>
      <TableCell>
        <Tooltip title={DICAS_DO_MOTIVO_DO_DIFF[item.motivo]}>
          <Chip size="small" color={COR_DO_MOTIVO[item.motivo]} variant="outlined" label={ROTULOS_DO_MOTIVO_DO_DIFF[item.motivo]} />
        </Tooltip>
      </TableCell>
      <TableCell>{item.antes !== null ? formatarValorEmReais(item.antes.valor) : '—'}</TableCell>
      <TableCell>{depois !== null ? formatarValorEmReais(depois.valor) : '—'}</TableCell>
      <TableCell>
        {depois === null ? null : aprovado ? (
          <Typography variant="caption" color="success.main">
            Aprovado
          </Typography>
        ) : (
          <Button
            size="small"
            disabled={emCurso}
            onClick={() => {
              definirEmCurso(true);
              definirErro(null);
              aoAprovarItem(depois.id)
                .then(() => {
                  definirAprovado(true);
                })
                .catch((causa: unknown) => {
                  definirErro(comoErroDaApi(causa).message);
                })
                .finally(() => {
                  definirEmCurso(false);
                });
            }}
          >
            Aprovar item
          </Button>
        )}
        {erro !== null ? (
          <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
            {erro}
          </Typography>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

/**
 * O que mudou nesta fonte contra a fonte ativa anterior do mesmo contrato
 * (`TP-050`, T10, `S7`): itens que entraram, saíram ou mudaram de valor, e
 * as regras ajustadas na revisão desta fonte (D6).
 */
export function DiffDaFonte({
  fonteId,
  aoAprovarItem,
}: {
  fonteId: string;
  aoAprovarItem: (itemId: string) => Promise<void>;
}): React.JSX.Element {
  const consulta = useQuery(opcoesDoDiff(fonteId));

  if (consulta.isPending) return <EstadoDeCarregamento linhas={4} />;

  if (consulta.isError) {
    return (
      <EstadoDeErro
        erro={consulta.error}
        contexto="Não foi possível carregar o diff da fonte"
        aoReprocessar={() => {
          void consulta.refetch();
        }}
      />
    );
  }

  const { primeiraFonte, atual, anterior, itens, regrasAjustadas } = consulta.data;
  const totais: Record<MotivoDoDiffDoItem, number> = { entrou: 0, saiu: 0, mudou: 0 };
  for (const item of itens) totais[item.motivo] += 1;
  const semItensNestaFonte = atual !== null && atual.totalDeItens === 0;

  return (
    <Stack spacing={1.5}>
      <Typography variant="h3">Itens: o que mudou</Typography>

      {anterior !== null ? (
        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            Comparado com a fonte ativa mais recente deste contrato:
          </Typography>
          <ReferenciaDaFonte fonte={anterior} />
        </Stack>
      ) : null}

      {primeiraFonte ? <Alert severity="info">Primeira fonte deste contrato: todo item aparece como novo.</Alert> : null}

      {itens.length > 0 ? (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap aria-label="Totais do diff">
          {(['entrou', 'mudou', 'saiu'] as const)
            .filter((motivo) => totais[motivo] > 0)
            .map((motivo) => (
              <Chip
                key={motivo}
                size="small"
                color={COR_DO_MOTIVO[motivo]}
                label={`${ROTULOS_DO_MOTIVO_DO_DIFF[motivo]}: ${String(totais[motivo])}`}
              />
            ))}
        </Stack>
      ) : null}

      {atual?.tipo === 'aditivo' && totais.saiu > 0 ? (
        <Alert severity="warning">
          Um aditivo pode trazer só parte dos itens: &quot;Não aparece&quot; não quer dizer que o item saiu do contrato.
        </Alert>
      ) : null}

      {itens.length === 0 ? (
        semItensNestaFonte ? (
          <EstadoVazio titulo="Nada para comparar" descricao="Esta fonte traz só regras, sem itens." />
        ) : (
          <EstadoVazio titulo="Itens iguais" descricao="Nenhum item mudou em relação à fonte comparada." />
        )
      ) : (
        <TableContainer>
          <Table aria-label="Itens que mudaram" size="small">
            <TableHead>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell>Unidade</TableCell>
                <TableCell>Página</TableCell>
                <TableCell>Motivo</TableCell>
                <TableCell>Valor — antes</TableCell>
                <TableCell>Valor — depois</TableCell>
                <TableCell>Aprovação individual</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {itens.map((item) => (
                <LinhaDoItem
                  key={item.depois?.id ?? item.antes?.id ?? item.codigo ?? 'sem-codigo'}
                  item={item}
                  aoAprovarItem={aoAprovarItem}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {regrasAjustadas.length > 0 ? (
        <Stack spacing={0.5}>
          <Typography variant="subtitle2">Regras ajustadas nesta revisão</Typography>
          {regrasAjustadas.map((regra) => (
            <Typography variant="body2" key={regra.regraId}>
              {ROTULOS_DE_TIPO_DE_REGRA[regra.tipo]} — substitui a regra anterior
            </Typography>
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
}
